// Calendar-mirror status of a session (server-side sync to the calendar
// domain): mirrored / error / cancelled. Hidden when the session has never
// been mirrored, unless `showPending` asks for a neutral "Not mirrored" pill.
import { useT } from '../lib/i18n';
import React from 'react';
import { AlertTriangle, CalendarCheck2, CalendarX2 } from 'lucide-react';
import type { MirrorStatus } from '@/services/learningService';

const CLS: Record<MirrorStatus | 'none', string> = {
  mirrored: 'bg-success-soft text-success-strong',
  error: 'bg-red-100 text-red-700 dark:bg-red-500/[0.16] dark:text-red-300',
  cancelled: 'bg-muted text-muted-foreground',
  none: 'bg-muted text-muted-foreground',
};

export const MirrorStatusBadge: React.FC<{ status?: MirrorStatus | null; error?: string | null; showPending?: boolean; className?: string }> = ({ status, error, showPending, className }) => {
  const t = useT();
  if (!status && !showPending) return null;
  const label = status === 'mirrored' ? t('training:mirror_mirrored', 'On calendar')
    : status === 'error' ? t('training:mirror_error', 'Calendar sync failed')
    : status === 'cancelled' ? t('training:mirror_cancelled', 'Removed from calendar')
    : t('training:mirror_none', 'Not on calendar');
  const Icon = status === 'mirrored' ? CalendarCheck2 : status === 'error' ? AlertTriangle : CalendarX2;
  return (
    <span data-testid="mirror-status-badge" data-status={status || 'none'} title={status === 'error' && error ? error : undefined}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${CLS[status || 'none']} ${className || ''}`}>
      <Icon className="h-3 w-3" aria-hidden /> {label}
    </span>
  );
};

export default MirrorStatusBadge;
